import React from 'react';
import { Box, Container, SxProps, Theme, Typography } from '@mui/material';

export interface HeroProps {
	/** Optional short label displayed above the heading. */
	eyebrow?: React.ReactNode;
	/** Main hero heading. */
	title: React.ReactNode;
	/** Supporting copy displayed below the heading. */
	description?: React.ReactNode;
	/** Buttons, links, or other content displayed below the description. */
	actions?: React.ReactNode;
	/** Optional background image. The background prop is used as its overlay. */
	backgroundImage?: string;
	/** Accepts any valid CSS background value. */
	background?: string;
	/** Shows the translucent rotated rectangle used by the blue hero design. */
	showDecoration?: boolean;
	/** Extra MUI styles for the decorative rectangle. */
	decorationSx?: SxProps<Theme>;
	color?: string;
	align?: 'left' | 'center' | 'right';
	contentMaxWidth?: number | string;
	minHeight?: number | string | Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number | string>>;
	maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
	sx?: SxProps<Theme>;
	contentSx?: SxProps<Theme>;
	className?: string;
}

const DEFAULT_BACKGROUND = [
	'radial-gradient(circle at 48% 42%, rgba(86, 174, 238, 0.7) 0%, rgba(35, 143, 225, 0.35) 34%, transparent 64%)',
	'linear-gradient(105deg, #0878dc 0%, #238cdd 45%, #0474d8 100%)'
].join(', ');

const Hero = ({
	eyebrow,
	title,
	description,
	actions,
	backgroundImage,
	background = DEFAULT_BACKGROUND,
	showDecoration = true,
	decorationSx,
	color = '#ffffff',
	align = 'left',
	contentMaxWidth = 560,
	minHeight = { xs: 320, sm: 360, md: 415 },
	maxWidth = 'lg',
	sx,
	contentSx,
	className
}: HeroProps) => {
	const contentAlignment = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
	const overlay =
		background.includes('gradient(') || background.includes('url(')
			? background
			: `linear-gradient(${background}, ${background})`;
	const backgroundValue = backgroundImage ? `${overlay}, url("${backgroundImage}")` : background;

	return (
		<Box
			component="section"
			className={className}
			sx={[
				{
					display: 'flex',
					alignItems: 'center',
					position: 'relative',
					isolation: 'isolate',
					minHeight,
					overflow: 'hidden',
					color,
					background: backgroundValue,
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
					backgroundSize: 'cover'
				},
				...(Array.isArray(sx) ? sx : [sx])
			]}
		>
			{showDecoration && (
				<Box
					aria-hidden="true"
					sx={[
						{
							position: 'absolute',
							zIndex: 0,
							top: { xs: -118, sm: -170, lg: -234 },
							left: { xs: '28%', sm: '32%', lg: 496 },
							width: { xs: 760, sm: 980, lg: 1284.494 },
							height: { xs: 330, sm: 440, lg: 569.932 },
							pointerEvents: 'none',
							background: 'rgba(251, 251, 251, 0.2)',
							// Figma's 167.16° orientation maps to this visual angle in CSS.
							transform: 'rotate(12.84deg)',
							transformOrigin: 'center',
							filter: 'blur(124px)',
							WebkitFilter: 'blur(124px)',
							willChange: 'transform, filter'
						},
						...(Array.isArray(decorationSx) ? decorationSx : [decorationSx])
					]}
				/>
			)}

			<Container
				maxWidth={maxWidth}
				sx={{ position: 'relative', zIndex: 1, width: '100%', px: { xs: 2.5, sm: 4, md: 5 } }}
			>
				<Box
					sx={[
						{
							display: 'flex',
							width: '100%',
							maxWidth: contentMaxWidth,
							py: { xs: 7, sm: 8, md: 10 },
							ml: align === 'right' ? 'auto' : align === 'center' ? 'auto' : 0,
							mr: align === 'center' ? 'auto' : 0,
							flexDirection: 'column',
							alignItems: contentAlignment,
							textAlign: align
						},
						...(Array.isArray(contentSx) ? contentSx : [contentSx])
					]}
				>
					{eyebrow && (
						<Typography
							component="div"
							sx={{
								mb: { xs: 1.5, md: 2 },
								fontFamily: 'DM Mono, monospace',
								fontSize: { xs: 13, sm: 14, md: 16 },
								fontWeight: 500,
								lineHeight: 1.5,
								letterSpacing: '0.12em',
								textTransform: 'uppercase'
							}}
						>
							{eyebrow}
						</Typography>
					)}

					<Typography
						component="h1"
						sx={{
							m: 0,
							fontFamily: 'DM Sans',
							fontSize: { xs: '2.25rem', sm: '3rem', md: '3.5rem' },
							fontWeight: 600,
							lineHeight: { xs: 1.08, md: 1.12 },
							letterSpacing: { xs: '-0.03em', md: '-0.04em' },
							overflowWrap: 'anywhere',
                            color:"#FFFFFF",
						}}
					>
						{title}
					</Typography>

					{description && (
						<Typography
							component="div"
							sx={{
								mt: { xs: 2.5, md: 3 },
								maxWidth: '48ch',
								fontFamily: 'DM Mono',
								fontSize: { xs: '0.875rem', sm: '1rem' },
								fontWeight: 400,
								lineHeight: 1.55,
								letterSpacing: '-0.01em',
								opacity: 0.94,
                                color:"#F7FBFF",
							}}
						>
							{description}
						</Typography>
					)}

					{actions && (
						<Box
							sx={{
								display: 'flex',
								width: { xs: '100%', sm: 'auto' },
								mt: { xs: 3, md: 4 },
								gap: 1.5,
								flexDirection: { xs: 'column', sm: 'row' },
								alignItems: { xs: 'stretch', sm: 'center' },
								justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
								'& > *': { width: { xs: '100%', sm: 'auto' } }
							}}
						>
							{actions}
						</Box>
					)}
				</Box>
			</Container>
		</Box>
	);
};

export default Hero;
